import { MutableRefObject, useCallback, useEffect, useRef } from 'react'
import type { ListImperativeAPI } from 'react-window'
import { ElementListItem } from '../types'

interface UseScrollSelectedIntoViewArgs {
  items: ElementListItem[]
  selectedIds: string[]
  listRef: MutableRefObject<ListImperativeAPI | null>
}

interface UseScrollSelectedIntoViewResult {
  /** Wire to the list's onRowsRendered so the scroll retries once the list has mounted. */
  handleRowsRendered: () => void
}

/**
 * Keeps the (single) selected row visible. Rows have a fixed height, so a row's offset only
 * changes with its index — deduplicating on (id, index) avoids re-scrolling on unrelated
 * re-renders after the user scrolled elsewhere.
 */
const useScrollSelectedIntoView = ({
  items,
  selectedIds,
  listRef,
}: UseScrollSelectedIntoViewArgs): UseScrollSelectedIntoViewResult => {
  const lastScrollKeyRef = useRef<string | null>(null)
  const selectedId = selectedIds.length === 1 ? selectedIds[0] : null

  const scrollSelectedIntoView = useCallback(() => {
    if (!selectedId) {
      lastScrollKeyRef.current = null
      return
    }
    const rowIndex = items.findIndex(item => item.id === selectedId)
    const listApi = listRef.current
    if (rowIndex === -1 || !listApi) return

    const scrollKey = `${selectedId}:${rowIndex}`
    if (lastScrollKeyRef.current === scrollKey) return
    lastScrollKeyRef.current = scrollKey

    listApi.scrollToRow({ index: rowIndex, align: 'auto', behavior: 'instant' })
  }, [items, listRef, selectedId])

  useEffect(() => {
    lastScrollKeyRef.current = null
  }, [selectedId])

  useEffect(() => {
    if (!selectedId) return
    const raf = window.requestAnimationFrame(scrollSelectedIntoView)
    return () => window.cancelAnimationFrame(raf)
  }, [scrollSelectedIntoView, selectedId])

  return { handleRowsRendered: scrollSelectedIntoView }
}

export default useScrollSelectedIntoView
